import { useMemo, useRef, useState, type ReactNode, type TouchEvent } from 'react';
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileIcon } from 'lucide-react';
import type { TaskAttachment, TaskDetail, TaskEvent, TaskMember } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useArrowKeyNav } from '@/hooks/use-arrow-key-nav';
import { formatBytes, formatDateTime } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { MentionText } from './MentionText';
import { memberHandle } from './task-ui';
import { isPlayableVideo } from './task-files';
import { OpenAttachmentContext } from './task-attachment-link';
import { upstreamOpens } from './task-upstream';
import { TASK_SOURCE_LABELS } from '@shared/types/task-sources';

type Slide = { attachment: TaskAttachment; event: TaskEvent };

/**
 * Lets any file on the task open in one viewer that steps through all of them, in the order they
 * were posted (so a "before" sent ahead of its "after" comes first).
 */
export function TaskAttachmentViewerProvider({
  task,
  members,
  onNavigate,
  children,
}: {
  task: TaskDetail;
  members: TaskMember[];
  onNavigate?: () => void;
  children: ReactNode;
}) {
  // The open file's id rather than its position, so a file arriving meanwhile doesn't shift it.
  const [openId, setOpenId] = useState<string | null>(null);
  const slides = useMemo(
    () => task.events.flatMap((event) => event.attachments.map((attachment) => ({ attachment, event }))),
    [task.events]
  );
  const index = openId ? slides.findIndex((s) => s.attachment.id === openId) : -1;

  return (
    <OpenAttachmentContext.Provider value={setOpenId}>
      {children}
      <TaskAttachmentViewer
        task={task}
        slides={slides}
        index={index}
        onIndexChange={(i) => setOpenId(slides[i]?.attachment.id ?? null)}
        onClose={() => setOpenId(null)}
        members={members}
        onNavigate={onNavigate}
      />
    </OpenAttachmentContext.Provider>
  );
}

/**
 * The file itself: a picture, a playable video, or (for anything else) a card to open it from.
 * `upstream` (T-99): the picture previews a file left upstream, which a click (or the card) opens.
 */
function Preview({ a, upstream }: { a: TaskAttachment; upstream?: { href: string; where: string } }) {
  const [failed, setFailed] = useState(false);
  if (a.is_image && !failed) {
    const img = <img src={a.url} alt={a.filename} className="max-h-full max-w-full object-contain" onError={() => setFailed(true)} />;
    return upstream ? (
      <a href={upstream.href} target="_blank" rel="noreferrer" data-hint={`Open on ${upstream.where}`} className="flex max-h-full max-w-full justify-center">
        {img}
      </a>
    ) : (
      img
    );
  }
  if (isPlayableVideo(a) && !failed) {
    return (
      <video src={a.url} controls playsInline className="max-h-full max-w-full bg-black" onError={() => setFailed(true)} />
    );
  }
  return (
    <div className="flex flex-col items-center gap-3 p-6 text-center">
      <FileIcon className="size-12 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        {failed ? 'This file couldn’t be loaded here.' : 'No preview for this kind of file.'}
      </p>
      <Button size="sm" asChild>
        <a href={upstream?.href ?? a.url} target="_blank" rel="noreferrer">
          <ExternalLink className="size-4" />
          {upstream ? `Open on ${upstream.where}` : 'Open file'}
        </a>
      </Button>
    </div>
  );
}

function TaskAttachmentViewer({
  task,
  slides,
  index,
  onIndexChange,
  onClose,
  members,
  onNavigate,
}: {
  task: TaskDetail;
  slides: Slide[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  members: TaskMember[];
  onNavigate?: () => void;
}) {
  const open = index >= 0;
  const slide = open ? slides[index] : null;
  const canGoPrev = index > 0;
  const canGoNext = index < slides.length - 1;
  const contentRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  useArrowKeyNav({ open, index, count: slides.length, onIndexChange });

  // Phones: swipe left for the next file, right for the previous (not on a video's own controls).
  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = e.touches.length === 1 && !(e.target instanceof HTMLVideoElement) ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    const t = e.changedTouches[0];
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(t.clientY - start.y) * 1.5) return;
    if (dx < 0 && canGoNext) onIndexChange(index + 1);
    if (dx > 0 && canGoPrev) onIndexChange(index - 1);
  };

  const stepButton = (dir: 'prev' | 'next', className?: string) => (
    <Button
      type="button"
      variant="secondary"
      size="icon"
      className={cn('shadow-sm disabled:opacity-40', className)}
      onClick={() => onIndexChange(dir === 'prev' ? index - 1 : index + 1)}
      disabled={dir === 'prev' ? !canGoPrev : !canGoNext}
      aria-label={dir === 'prev' ? 'Previous file' : 'Next file'}
    >
      {dir === 'prev' ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />}
    </Button>
  );

  const a = slide?.attachment;
  const event = slide?.event;
  const author = event ? members.find((m) => m.user_id === event.actor_user_id) : undefined;
  // T-99: a preview of a file left upstream opens that file, and has nothing of its own to download.
  const opens = a && event ? upstreamOpens(event)[a.id] : undefined;
  const upstream = opens ? { href: opens, where: task.upstream ? TASK_SOURCE_LABELS[task.upstream.kind] : 'the source' } : undefined;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        ref={contentRef}
        tabIndex={-1}
        // Focus the viewer itself rather than its first button, so no button shows a focus ring.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
        className="flex h-[calc(100dvh-1rem)] max-h-none max-w-[calc(100%-1rem)] flex-col gap-3 p-3 sm:h-[calc(100dvh-4rem)] sm:max-w-6xl sm:p-5"
      >
        {a && event ? (
          <>
            <DialogHeader className="shrink-0 gap-1 pr-8 text-left">
              <DialogTitle className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 text-base leading-snug sm:text-lg">
                <span className="min-w-0 break-all">{a.filename}</span>
                {slides.length > 1 ? (
                  <span className="shrink-0 text-sm font-normal text-muted-foreground">
                    {index + 1} of {slides.length}
                  </span>
                ) : null}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {memberHandle(author)}
                {event.via_api_key_name ? ` via ${event.via_api_key_name}` : ''} · {formatDateTime(new Date(a.created_at))} ·{' '}
                {formatBytes(a.size)}
              </DialogDescription>
              {event.body ? (
                <MentionText
                  text={event.body}
                  members={members}
                  contexts={task.contexts}
                  onNavigate={() => {
                    onClose();
                    onNavigate?.();
                  }}
                  className="line-clamp-2 text-sm text-muted-foreground"
                />
              ) : null}
            </DialogHeader>

            <div className="relative min-h-0 flex-1" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              {/* Beside the file on wider screens; phones have them under it, clear of the picture. */}
              {slides.length > 1 ? (
                <>
                  {stepButton('prev', 'absolute top-1/2 left-2 z-10 hidden -translate-y-1/2 sm:inline-flex')}
                  {stepButton('next', 'absolute top-1/2 right-2 z-10 hidden -translate-y-1/2 sm:inline-flex')}
                </>
              ) : null}
              <div className="flex h-full items-center justify-center overflow-hidden rounded-lg border bg-muted/40">
                <Preview key={a.id} a={a} upstream={upstream} />
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {slides.length > 1 ? (
                <div className="flex gap-2 sm:hidden">
                  {stepButton('prev')}
                  {stepButton('next')}
                </div>
              ) : null}
              {upstream ? (
                <Button variant="outline" size="sm" className="ml-auto" asChild>
                  <a href={upstream.href} target="_blank" rel="noreferrer">
                    <ExternalLink className="size-4" />
                    Open on {upstream.where}
                  </a>
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" className="ml-auto" asChild>
                    <a href={a.url} target="_blank" rel="noreferrer">
                      <ExternalLink className="size-4" />
                      <span className="sm:hidden">Open</span>
                      <span className="hidden sm:inline">Open in new tab</span>
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a href={a.url} download={a.filename}>
                      <Download className="size-4" />
                      Download
                    </a>
                  </Button>
                </>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
